import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { Box, css, useMediaQuery, useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { fonts } from '@theme/fontsCustomer';
import { BulletIcon } from '@icons/BulletIcon';
import { useGetCategories } from '@queryHooks/index';
import { transientOptions } from '@utils/index';
import { useProviderStoreContext } from './ProviderStoreContext';

/**
 * 판매자 스토어 카테고리 탭 헤더 + Outlet
 * Store.tsx와 동일한 UI 구조 (카트/매니저 기능 제외)
 */
export const ProviderStore: React.FC = observer(() => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const paths = pathname.split('/').filter(p => p);
  const currentPath = decodeURIComponent(paths[paths.length - 1] ?? '');
  const isDetailPage = paths.includes('product');
  const theme = useTheme();
  const { colors } = theme;
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const { providerId } = useProviderStoreContext();
  const { data: rootCategories } = useGetCategories({ isRootCategory: true, providerId });
  const { CUSTOM_ROOT_CATEGORY } = getEnvConfig();
  const supportsCustomCategory = CUSTOM_ROOT_CATEGORY?.toLowerCase() === 'true';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <PageContainer className={isDetailPage ? 'detail' : ''}>
      {isDetailPage ? null : (
        <Container>
          <TabsWrapper>
            {rootCategories &&
              rootCategories.content
                .filter(
                  category => supportsCustomCategory || category.name === 'Software' || category.name === 'Hardware',
                )
                .map((category, index) => (
                  <Fragment key={category.id}>
                    {index !== 0 && !matches && <BulletIcon color={colors['text-w-opacity-60%']} />}
                    <PublicMainCategoryLink
                      key={category.id}
                      to={`category/${encodeURIComponent(category.name)}`}
                      state={{ subTitle: 'Store' }}
                    >
                      <CategoryLinkTitle $isSelected={currentPath === category.name}>
                        {category.name === 'Software'
                          ? t('Subscrib_Detail_Overview_11')
                          : category.name === 'Hardware'
                          ? t('Subscrib_Detail_Overview_12')
                          : category.name}
                      </CategoryLinkTitle>
                    </PublicMainCategoryLink>
                  </Fragment>
                ))}
          </TabsWrapper>
        </Container>
      )}
      <Outlet />
    </PageContainer>
  );
});

const Container = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: ${({ theme }) => theme.colors['bg-primary']};
`;

const TabsWrapper = styled(Box)`
  max-width: 1280px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 34px;
  padding: 12px 20px;
  position: relative;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    height: 48px;
    gap: 48px;
    padding: 0;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #fafbfc;
  &.detail {
    height: max-content;
    background-color: #fff;
  }
`;

const PublicMainCategoryLink = styled(Link)`
  height: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 0px;
  text-decoration: unset;
`;

const CategoryLinkTitle = styled('span', transientOptions)<{
  $isSelected: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  ${fonts.Headline7};
  color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors['text-white'] : theme.colors['text-w-opacity-60%'])};
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    height: 100%;
    ${({ $isSelected, theme }) =>
      $isSelected &&
      css`
        &::after {
          content: '';
          display: block;
          width: 100%;
          height: 3px;
          position: absolute;
          bottom: 0;
          left: 0;
          border-radius: 2px 2px 0px 0px;
          background-color: ${theme.colors['bg-white']};
        }
      `};
  }
`;
